import cn from 'classnames';
import { useEffect, useContext, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useLocation } from 'react-router';

import Text from 'components/common/Text';
import Loading from 'components/icons/Loading';
import { UserContext, PickYourMealsContext } from 'components/signUpFunnel/Contexts';
import LegalCopy from 'components/signUpFunnel/LegalCopy';
import FunnelHeader from 'components/signUpFunnel/shared/FunnelHeader';
import RedesignedThankYouGreeting from 'components/signUpFunnel/thankYou/RedesignedThankYouGreeting';
import ThankYouSubscriptionStatus from 'components/signUpFunnel/thankYou/ThankYouSubscriptionStatus';
import { BrandContext } from 'context/BrandContext';
import { brandData } from 'helpers/BrandData';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useAnalyticsEffect from 'hooks/useAnalyticsEffect';
import useBrand from 'hooks/useBrand';
import useFetchExperimentAssignment from 'hooks/useFetchExperimentAssignment';
import useFunnelExperimentAssignment from 'hooks/useFunnelExperimentAssignment';
import useFunnelMealPlan from 'hooks/useFunnelMealPlan';
import useMetadata from 'hooks/useMetadata';
import useScreenSizeConstants from 'hooks/useScreenSizeConstants';
import { TEMPO, PAGE_VIEW_EVENT, THANK_YOU_PAGE_REDESIGN } from 'lib/constants';
import RequestService from 'services/RequestService';
import styles from 'styles/components/signUpFunnel/thankYou/ThankYouContainer';

import InviteFriendsInstructions from './InviteFriendsInstructions';
import NextSteps from './NextSteps';
import PickYourMealsHero from './PickYourMealsHero';
import ShareInvitations from './ShareInvitations';

const ThankYouContainer = () => {
  const location = useLocation();
  const { user, setUser } = useContext(UserContext);
  const { brand } = useContext(BrandContext);
  const { isPickYourMeals } = useContext(PickYourMealsContext);
  const { isUnderBp1: isSmartphoneScreenSize } = useScreenSizeConstants();
  const mealPlanSlug = useFunnelMealPlan();
  const firstName = useMemo(() => user?.firstName, [user]);
  const isTempo = useBrand(TEMPO);
  const experimentAssignment = useFunnelExperimentAssignment();
  const { isVariant: isInRedesignedThankYouPageExperiment } =
    useFetchExperimentAssignment(THANK_YOU_PAGE_REDESIGN);

  const PickYourMealsGreeting = () => (
    <div className="flex flex-col items-center">
      <Text brandStylesOverrides="h1 text-tempo-black-100">Nicely done!</Text>
      <Text brandStylesOverrides="h1 text-tempo-black-100">Here’s what’s next:</Text>
    </div>
  );

  useMetadata({ title: `Step 6: Thank You | ${brandData(brand)?.displayName}` }, false);

  const onContinue = useAnalyticsCallback(
    null,
    ({ logEvent, events }) => {
      if (brand) {
        if (isPickYourMeals) {
          logEvent(events.pickMeals[TEMPO].thankYou.submitted, {
            mealPlanSlug: mealPlanSlug || TEMPO,
            ...experimentAssignment,
          });
        } else {
          logEvent(events.signUp[brand].thankYou.submitted, {
            mealPlanSlug: mealPlanSlug,
            ...experimentAssignment,
          });
        }
      }
    },
    [brand, mealPlanSlug, isPickYourMeals, experimentAssignment]
  );

  useAnalyticsEffect(
    ({ logEvent, events }) => {
      if (brand) {
        if (isPickYourMeals) {
          logEvent(events.pickMeals[TEMPO].thankYou.viewed, {
            mealPlanSlug: TEMPO,
            ...experimentAssignment,
          });
        } else {
          logEvent(events.signUp[brand].thankYou.viewed, experimentAssignment);
        }
      }
    },
    [brand, mealPlanSlug, isPickYourMeals, experimentAssignment]
  );

  useEffect(() => {
    RequestService.v3.createEvents({ params: { eventName: PAGE_VIEW_EVENT } });
  }, []);

  useEffect(() => {
    if (location.state) setUser(user => ({ ...user, ...location.state.user }));
  }, [location.state]);

  const DefaultUserGreeting = () => {
    return (
      <FunnelHeader
        isCentered
        containerClassName={cn('px-4 pb-6 pt-12 bp1:px-0', {
          'bg-tempo-gray-100': isPickYourMeals,
        })}
        title={
          isPickYourMeals ? (
            <PickYourMealsGreeting />
          ) : firstName ? (
            `Welcome, ${firstName}!`
          ) : (
            <Skeleton count={2} className="px-4" />
          )
        }
      />
    );
  };

  const InvitationSection = () => {
    return (
      <>
        <section className="bp2:hidden">
          <ShareInvitations />
          <InviteFriendsInstructions isPickYourMeals={isPickYourMeals} />
        </section>

        <div className="mx-auto hidden w-full max-w-5xl flex-row bp2:flex">
          <div className="w-1/2">
            <ShareInvitations />
          </div>
          <div className="w-1/2">
            <InviteFriendsInstructions isPickYourMeals={isPickYourMeals} />
          </div>
        </div>
      </>
    );
  };

  const SmartphoneUserGreeting = () => {
    return (
      <FunnelHeader
        isCentered
        containerClassName="py-6"
        title={
          isPickYourMeals ? (
            <PickYourMealsGreeting />
          ) : firstName ? (
            `Welcome, ${firstName}!`
          ) : (
            "We're glad you've joined us!"
          )
        }
      />
    );
  };

  const SmartphoneInvitationSection = () => {
    return (
      <section className={cn(styles.smartphoneInvitationSection, 'relative flex flex-col')}>
        <ShareInvitations isSmartphoneScreenSize={isSmartphoneScreenSize} />
        <InviteFriendsInstructions
          isSmartphoneScreenSize={isSmartphoneScreenSize}
          isPickYourMeals={isPickYourMeals}
        />
      </section>
    );
  };

  const SmartphoneThankYouPage = () => {
    return (
      <section
        className={cn('px-6 bp1:hidden', isPickYourMeals ? 'bg-tempo-gray-40' : 'bg-transparent')}
      >
        {isPickYourMeals ? (
          <PickYourMealsHero onContinue={onContinue} />
        ) : isInRedesignedThankYouPageExperiment ? (
          <>
            <RedesignedThankYouGreeting firstName={firstName} />
            <div className="h-[440px] w-full" />
            <ThankYouSubscriptionStatus />
          </>
        ) : (
          <>
            <SmartphoneUserGreeting />
            <NextSteps onClick={onContinue} isSmartphoneScreenSize={isSmartphoneScreenSize} />
          </>
        )}
        <SmartphoneInvitationSection />
        <LegalCopy onClick={onContinue} isSmartphoneScreenSize={isSmartphoneScreenSize} />
      </section>
    );
  };

  const DefaultThankYouPage = () => {
    return (
      <section className={cn('hidden bp1:block', { 'bg-tempo-gray-40': isPickYourMeals })}>
        {isPickYourMeals ? (
          <PickYourMealsHero onContinue={onContinue} />
        ) : isInRedesignedThankYouPageExperiment ? (
          <>
            <RedesignedThankYouGreeting firstName={firstName} />
            <div className="mx-auto grid w-full max-w-6xl grid-cols-4 gap-8 self-stretch px-4 pb-8 bp2:px-8">
              <div className="col-span-3 h-[400px] w-full" />
              <ThankYouSubscriptionStatus />
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center px-20">
            <div
              className={cn(
                'my-6 flex w-full max-w-5xl flex-col rounded-2xl border border-solid bp1:px-6',
                isTempo ? 'border-charcoal-80 bg-white-100' : 'border-cream-120 bg-cream-110'
              )}
            >
              <DefaultUserGreeting />
              <NextSteps onClick={onContinue} />
            </div>
          </div>
        )}
        <InvitationSection />
        <LegalCopy onClick={onContinue} />
      </section>
    );
  };

  return user ? (
    <>
      <SmartphoneThankYouPage />
      <DefaultThankYouPage />
    </>
  ) : (
    <Loading />
  );
};

export default ThankYouContainer;
